'use client'
import React, { useEffect, useCallback, useMemo } from 'react'
import Link from 'next/link'
import ImageComponent from '@/src/components/common/ImageComponent'
import { ArrowUpRightSVG } from '@/src/ui/AllSVG'
import { getCategoriesPath, getShopCategoryPath, getSingleProductPath } from '@/src/routes/routes'
import { ChildrenCategoriesEntity, IMegaMenuCategoriesAPIResponseData, IMostWantedProductsEntity } from '@/src/types/megaMenu'

interface MegaMenuProps {
    megaMenuData: IMegaMenuCategoriesAPIResponseData[];
    activeNavItem: string | null
    isOpen: boolean
    onClose: () => void
    onPanelMouseEnter?: () => void
    onPanelMouseLeave?: () => void
}


export default function MegaMenu({ megaMenuData, activeNavItem, isOpen, onClose, onPanelMouseEnter, onPanelMouseLeave }: MegaMenuProps) {
    const base = getCategoriesPath()
    const selectedCategory = megaMenuData.find((item) => item.name === activeNavItem)
    const HIGHLIGHTS = [
        { label: 'Exclusive ', italic: 'Products', href: `${base}?category=${encodeURIComponent(selectedCategory?.slug ?? '')}&is_exclusive=true` },
        { label: 'Under 499', italic: '', href: `${base}?category=${encodeURIComponent(selectedCategory?.slug ?? '')}&priceMin=0&priceMax=500` },
        { label: 'Most Wanted', italic: '', href: `${base}?category=${encodeURIComponent(selectedCategory?.slug ?? '')}&sort=best_seller` },
        { label: 'New  ', italic: 'Arrivals', href: `${base}?category=${encodeURIComponent(selectedCategory?.slug ?? '')}&sort=new_arrival` },
    ]

    const { categoriesLinks, featuredProducts, brandsLinks } = useMemo(() => {
        if (!selectedCategory) {
            return {
                categoriesLinks: [] as { label: string; href: string }[],
                featuredProducts: [] as IMostWantedProductsEntity[],
                brandsLinks: [] as { label: string; href: string }[],
            }
        }

        const children = (selectedCategory.children_categories ?? []).filter((c): c is ChildrenCategoriesEntity => c != null)
        const viewAll = { label: 'View All Categories', href: getShopCategoryPath(selectedCategory.slug) }
        const childLinks = children.length > 0 ? children.map((child) => ({
            label: child.name,
            href: getShopCategoryPath(selectedCategory.slug, child.slug),
        })) : []
        const categoriesLinks = [viewAll, ...childLinks]
        const featuredProducts = selectedCategory.most_wanted_products?.length
            ? selectedCategory.most_wanted_products.slice(0, 2)
            : []
        const brands = (selectedCategory?.brands ?? []).filter((b): b is NonNullable<typeof b> => b != null)
        const brandsLinks = [
            { label: 'View All Brands', href: `${base}?category=${encodeURIComponent(selectedCategory.slug)}` },
            ...brands.map((brand) => ({
                label: brand.name,
                href: `${base}?category=${encodeURIComponent(selectedCategory.slug)}&brand=${encodeURIComponent(brand.slug)}`,
            })),
        ]
        return { categoriesLinks, featuredProducts, brandsLinks }
    }, [megaMenuData, activeNavItem, base])


    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose()
    }, [onClose])

    useEffect(() => {
        if (isOpen) document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isOpen, handleKeyDown])

    return (
        <>
            <div className={`fixed inset-0 z-40 pointer-events-none transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}`}>
                {/* Backdrop — visual only; does not keep menu open on hover */}
                <div className="absolute inset-0 bg-black/50 pointer-events-none" />

                {/* Panel */}
                <div
                    className={`mx-4 my-28 rounded-xl overflow-hidden relative bg-[#E7E6E6CC] border border-muted-color backdrop-blur-[5.45px] shadow-[0_8px_40px_0_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out ${isOpen ? 'translate-y-0 pointer-events-auto' : '-translate-y-20 pointer-events-none'}`}
                    onMouseEnter={onPanelMouseEnter}
                    onMouseLeave={onPanelMouseLeave}
                >
                    <div className="custom_container py-8">
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_0.9fr_1.8fr] gap-8 lg:gap-12">

                            {/* Highlights */}
                            <div className="space-y-4">
                                <h3 className="text-sm border-b border-muted-color pb-3">Highlights</h3>
                                <ul className="space-y-1">
                                    {HIGHLIGHTS && HIGHLIGHTS?.length > 0 && HIGHLIGHTS?.map((item, index) => (
                                        <li key={`${item?.href}-${index}`}>
                                            <Link
                                                href={item?.href}
                                                onClick={onClose}
                                                className="group inline-block text-2xl font-semibold hover:opacity-50 transition-opacity duration-200 capitalize"
                                            >
                                                {item?.label}
                                                {item?.italic && (
                                                    <em className="font-secondary italic">{item?.italic}</em>
                                                )}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Categories */}
                            <div className="space-y-4">
                                <h3 className="text-sm border-b border-muted-color pb-3">Categories</h3>
                                <ul className="space-y-2.5">
                                    {categoriesLinks && categoriesLinks?.length > 0 && categoriesLinks?.map((item, index) => (
                                        <li key={`${item?.href}-${index}`}>
                                            <Link
                                                href={item?.href}
                                                onClick={onClose}
                                                className="text-sm font-medium hover:opacity-50 transition-opacity duration-200 block capitalize"
                                            >
                                                {item?.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Brands */}
                            <div className="space-y-4">
                                <h3 className="text-sm border-b border-muted-color pb-3">Brands</h3>
                                <ul className="space-y-2.5 h-[calc(1.25rem*9+0.625rem*8)] overflow-y-auto custom_scrollbar pr-1">
                                    {brandsLinks && brandsLinks?.length > 0 && brandsLinks?.map((item, index) => (
                                        <li key={`${item?.href}-${index}`}>
                                            <Link
                                                href={item?.href}
                                                onClick={onClose}
                                                className="text-sm font-medium hover:opacity-50 transition-opacity duration-200 block capitalize"
                                            >
                                                {item?.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Featured Products */}
                            <div className="grid grid-cols-2 gap-3">
                                {featuredProducts && featuredProducts?.length > 0 && featuredProducts?.map((product) => (
                                    <Link
                                        key={product?._id}
                                        href={getSingleProductPath(product?.slug ?? '')}
                                        onClick={onClose}
                                        className="group block space-y-2.5"
                                    >
                                        <div className="relative aspect-4/5 w-full bg-secondary-background-color rounded-xl overflow-hidden">
                                            <ImageComponent
                                                src={product?.variants?.[0]?.images?.[0] || product?.thumbnail}
                                                alt={product?.title}
                                                object_cover={false}
                                                priority={false}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-medium truncate">{product?.title}</p>
                                            <p className="text-[10px] tracking-[0.14px] uppercase flex items-center gap-1 underline underline-offset-2 group-hover:opacity-60 transition-opacity duration-200">
                                                Explore Now
                                                <ArrowUpRightSVG className="text-body-color" />
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
